<script>
import { mapState } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import CarTypeModel from '@models/CarType';
import debounce from "lodash/debounce";
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.car_types'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    HeaderComponent,
  },
  data() {
    return {
      sortBy: 'name',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', sortable: true },
        { key: 'avatar', label: this.$t('table_labels.avatar'), sortable: false },
        { key: 'color', label: this.$t('table_labels.color'), sortable: false },
        { key: 'name', label: this.$t('table_labels.name'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      filters: {
        name: null,
      },
      isBusy: false,
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
    }),
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    updateFilter: debounce(function(value) {
      this.filters.name = value;
    }, 500),
    itemsProvider(ctx) {
      return CarTypeModel.orderBy(this.sortDir + ctx.sortBy)
        .where('name', this.filters.name)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.car_type') }))) {
        this.$wait.start('car type processing');
        new CarTypeModel({ id })
          .delete()
          .then(() => {
            this.$wait.end('car type processing');
            this.$refs.asyncTable.refresh();
          })
          .catch(() => {
            this.$wait.end('car type processing');
          });
      }
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="$t('titles.car_types')" :button-text="$t('titles.create_car_type')" @button-pressed="$router.push({ name: 'carTypes.create' })"></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col sm="12" md="12" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span>&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: [
                    $t('search.entities.name'),
                  ].join(', '),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.name" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter">&nbsp;</b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.name" @click="filters.name = ''">{{ $t('actions.clear') }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <div id="cars-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('car type processing') || isBusy || $wait.is('downloading pdf')"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
      <!-- custom sorting icons -->
      <template #head()="{ label, field: { key, sortable } }">
              {{ label }}
              <template v-if="sortable">
                <!-- Custom icons -->
                <template>
                  <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
                  <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
                  <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
                </template>
              </template>
        </template>
       <template v-slot:cell(avatar)="row">
        <b-avatar
            v-if="row.item.avatar"
            size="30px"
            :src="row.item.avatar.small"
            alt="Avatar"
          />
       </template>
       <template v-slot:cell(color)="row">
         <span class="rounded-circle" :style="`background-color: ${row.item.color}; height: 30px; width: 30px;display:inline-block;`"></span>
       </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'carTypes.edit', params: { id: row.item.id } }">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
